import { useState } from 'react';
import { Mail, MessageSquare, FileText, Cctv } from 'lucide-react';
import { motion } from 'framer-motion';
import { TopBar } from './TopBar';
import { Sidebar, SidebarItem } from './Sidebar';
import { EmailApp } from '../email';
import { ChatApp } from '../chat';
import { FileApp } from '../file';
import { Control } from '../control/Control';
import { PasswordDialog } from '../../ui/PasswordDialog';
import { Cover } from '../scene/Cover';
import { useGameStore } from '../../../stores/gameStore';
import { playSound } from '../../../utils/soundService';

const sidebarItems: SidebarItem[] = [
  {
    id: 'control',
    label: 'Control Center',
    icon: <Cctv size={24} strokeWidth={1.5} />,
  },
  {
    id: 'email',
    label: 'Email',
    icon: <Mail size={24} strokeWidth={1.5} />,
  },
  {
    id: 'chat',
    label: 'Chat',
    icon: <MessageSquare size={24} strokeWidth={1.5} />,
  },
  {
    id: 'files',
    label: 'Files',
    icon: <FileText size={24} strokeWidth={1.5} />,
  },
];

export function Layout() {
  const [activeItem, setActiveItem] = useState<string | null>('control');
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [selectedLockedItem, setSelectedLockedItem] = useState<string | null>(
    null,
  );
  const [passwordError, setPasswordError] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const { sidebarLocked, unlockSidebar, loggedIn } = useGameStore();

  const handleItemClick = (itemId: string) => {
    if (sidebarLocked.includes(itemId)) {
      playSound('sidebar-locked');
      setSelectedLockedItem(itemId);
      setShowPasswordDialog(true);
    } else {
      playSound('sidebar-switch');
      setActiveItem(itemId);
    }
  };

  const handlePasswordSubmit = (password: string) => {
    if (selectedLockedItem === 'email' && password === '1766') {
      playSound('success');
      setPasswordSuccess(true);
      setTimeout(() => {
        unlockSidebar('email');
        setActiveItem('email');
        setShowPasswordDialog(false);
        setSelectedLockedItem(null);
        setPasswordSuccess(false);
        setPasswordError(false);
      }, 1500);
    } else {
      playSound('password-fail');
      setPasswordError(true);
      setTimeout(() => setPasswordError(false), 400);
    }
  };

  const itemsWithActive = sidebarItems.map((item) => ({
    ...item,
    active: item.id === activeItem,
  }));

  return (
    <div className="h-screen w-screen flex flex-col bg-white overflow-hidden relative">
      <motion.div
        className="absolute inset-0 z-50"
        initial={{ y: 0 }}
        animate={{ y: loggedIn ? '-100%' : 0 }}
        transition={{ duration: 0.6, ease: 'easeInOut' }}
      >
        <Cover />
      </motion.div>

      <TopBar title="laura.ai" />
      <div className="flex flex-1 overflow-hidden">
        {sidebarItems.length > 0 && (
          <Sidebar items={itemsWithActive} onItemClick={handleItemClick} />
        )}
        <div className="flex-1 overflow-auto bg-white">
          {activeItem === 'email' && <EmailApp />}
          {activeItem === 'chat' && <ChatApp />}
          {activeItem === 'files' && <FileApp />}
          {activeItem === 'control' && <Control />}
        </div>
      </div>

      <PasswordDialog
        isOpen={showPasswordDialog}
        title={
          selectedLockedItem
            ? `Unlock ${sidebarItems.find((item) => item.id === selectedLockedItem)?.label}`
            : 'Unlock'
        }
        onSubmit={handlePasswordSubmit}
        hint="employee id"
        isError={passwordError}
        isSuccess={passwordSuccess}
        onCancel={() => {
          setShowPasswordDialog(false);
          setSelectedLockedItem(null);
          setPasswordError(false);
        }}
      />
    </div>
  );
}
